import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Container, Modal, Nav, Offcanvas, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import TextAreaBox from "../../components/InputBox/TextAreaBox";
import { FlightFplFile, IfrValidation, isIfrValidationCurrentAndOk } from "../../models/flight";
import FlatPicker from "react-flatpickr";
import moment from "moment";
import { Player } from "@lottiefiles/react-lottie-player";
import { useAppSelector } from "../../store/hooks";
import { selectedOrganization } from "../../store/organizationsSlice";
import { IfrPlanningOverrides, IfrRouteResult, ValidateFplResponse } from "../../avitracerApi";
import IfpsStateBadge from "../../components/IfpsStateBadge";
import PlanIfrRouteModal from "../../components/PlanIfrRouteModal";
import IfpsMessagesList from "../../components/IfpsMessagesList";

export interface BaseFplData {
  fplFile: FlightFplFile;
  flightPlanPicDetails: string;
  departureDateTime: Date;
  flightId: string;
  isIfr?: boolean;
  ifpsRoute?: string;
  altn1?: string;
  ifrFuelPerHour?: number;
  ifrMaxMass?: number;
  ifrValidation?: IfrValidation; //persisted IFPUV validation — gates filing for non-VFR plans
}

// Normalized result of an IFR route-select used by BaseFplPage, regardless of whether the
// underlying entity is a Flight or a FlightPlan.
export type BaseIfrSelectResult = { fplFile?: FlightFplFile; ifpsRoute?: string };

// One-line "what matters" summary for a collapsed IFPS message row — the actionable datum for each ORM
// type: the assigned CTOT (slot) for SAM/SRM, the first error for a rejection (REJ), the suggested route
// for a suspension (FLS), the IFPL id for an acceptance (ACK), otherwise the plain-language meaning.
interface BaseFplPageProps {
  data: BaseFplData;
  getPdfDoc: () => Promise<any>;
  fileFplUsingAviTracerAction: () => Promise<any>;
  refileFplAction: () => Promise<any>;
  chgFplAction: (f15: string, f18: string) => Promise<any>;
  cancelFiledFplAction: () => Promise<any>;
  delayFiledFplAction: (newTime: Date) => Promise<any>;
  findIfrRoutesAction?: (optimizes: string[], levels?: number[], overrides?: IfrPlanningOverrides) => Promise<IfrRouteResult[]>;
  checkManualRouteAction?: (field15: string, overrides?: IfrPlanningOverrides) => Promise<IfrRouteResult>;
  //initial editable planning inputs shown in the route planner
  ifrPlanningDefaults?: { departure?: string; destination?: string; alternate1?: string; alternate2?: string };
  selectIfrRouteAction?: (field15: string, overrides?: IfrPlanningOverrides) => Promise<BaseIfrSelectResult>;
  validateFplAction?: () => Promise<ValidateFplResponse>;
  //Apply an IFPS-proposed route (POSRTE/NEWRTE) to a filed IFR plan — backend re-validates then sends a
  //CHG (suspended) or a fresh FPL (rejected).
  applyProposedRouteAction?: (route: string) => Promise<any>;
  goBack: () => void
  goToSchedule?: () => void
}

function BaseFplPage({ data, getPdfDoc, fileFplUsingAviTracerAction, refileFplAction, chgFplAction, cancelFiledFplAction, delayFiledFplAction, findIfrRoutesAction, checkManualRouteAction, selectIfrRouteAction, validateFplAction, applyProposedRouteAction, ifrPlanningDefaults, goBack, goToSchedule }: BaseFplPageProps) {

  const { flightPlanPicDetails, departureDateTime, flightId } = data;

  const [fplFile, setFplFile] = useState<FlightFplFile>(data.fplFile);
  const [ifpsRoute, setIfpsRoute] = useState<string | undefined>(data.ifpsRoute);

  const isIfr = data.isIfr ?? false;
  const missingIfrAlternate = isIfr && !(data.altn1 && data.altn1.length > 0);
  const missingIfrPerformance = isIfr && (!data.ifrFuelPerHour || !data.ifrMaxMass);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const ifrIsFiled = fplFile.status === "filed" || fplFile.status === "delayed";
  //IFPS tracking lives on both filed and delayed plans (a DLA keeps the plan active at EUROCONTROL).
  const ifrIfps = fplFile.status === "filed" || fplFile.status === "delayed" ? fplFile.ifps : undefined;

  //On desktop, IFR flights show the route/validation/IFPS-messages and the FPL form (PDF) as tabs in the
  //right pane — the IFPS side is the default since it carries the actionable status. VFR just shows the PDF.
  const [rightTab, setRightTab] = useState<"ifps" | "pdf">("ifps");

  //On the file page the route is fixed (never changed before filing) — only re-validate it here.
  //The current FPL string (for staleness checks against the stored IFPUV validation). Derived from the
  //fplFile STATE (not the loader prop) so it tracks a route re-select done on this page.
  const currentFplCode = (fplFile.status === "ready-to-file" || fplFile.status === "scheduled") ? fplFile.fplCode : undefined;
  const [validating, setValidating] = useState(false);
  //Seed from the persisted validation when it matches the current FPL, so the result shows immediately
  //(and gates filing) without waiting for a re-run.
  const [validation, setValidation] = useState<ValidateFplResponse["validation"] | undefined>(
    data.ifrValidation && currentFplCode && data.ifrValidation.fplCode === currentFplCode
      ? { result: data.ifrValidation.result, validation: data.ifrValidation.validation, errors: data.ifrValidation.errors }
      : undefined
  );
  //Non-VFR plans may only be filed once IFPUV validation of the current FPL passes. Use this session's
  //validation result if present (it reflects the current FPL), else the persisted one.
  const ifrValidationOk = validation ? validation.result === "ok" : isIfrValidationCurrentAndOk(data.ifrValidation, currentFplCode);
  //Block filing while an (auto-)validation is in flight too, so a stale result can't leak the button open.
  const ifrBlocksFiling = isIfr && (validating || !ifrValidationOk);
  const handleValidate = async () => {
    if (!validateFplAction) return;
    setValidating(true);
    setValidation(undefined);
    try {
      const resp = await validateFplAction();
      setValidation(resp.validation);
    } catch (e: any) {
      setValidation({ result: "errors", validation: typeof e === "string" ? e : e?.message ?? "Validation failed.", errors: [] });
    } finally {
      setValidating(false);
    }
  };

  //Auto-validate a non-VFR ready-to-file plan on open, so the file status reflects IFPS acceptance (and the
  //filing gate resolves) without a manual click. Runs once; skipped when a current validation is seeded.
  useEffect(() => {
    if (isIfr && data.fplFile.status === "ready-to-file" && ifpsRoute && !validation && validateFplAction) {
      handleValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [icaoPdfData, setIcaoPdfData] = useState<Blob | null>(null);

  //One object URL per blob, revoked on change/unmount. Creating it inline in JSX would mint a new
  //never-revoked URL on EVERY render — and a changing iframe src reloads the PDF on every state tick.
  const icaoPdfUrl = useMemo(() => (icaoPdfData ? window.URL.createObjectURL(icaoPdfData) : undefined), [icaoPdfData]);
  useEffect(() => {
    return () => {
      if (icaoPdfUrl) window.URL.revokeObjectURL(icaoPdfUrl);
    };
  }, [icaoPdfUrl]);

  const selectedOrg = useAppSelector(selectedOrganization);

  const [searchParams] = useSearchParams();
  const [showCancelFplModal, setShowCancelFplModal] = useState(searchParams.has("cancel"));
  const [showDelayFplModal, setShowDelayFplModal] = useState(searchParams.has("delay"));
  const [showRefileFplModal, setShowRefileFplModal] = useState(false);
  const [showChgFplModal, setShowChgFplModal] = useState(searchParams.has("change"));

  const [showSendingAFTN, setShowSendingAFTN] = useState(false);

  //Inline error for the file/refile/chg/cancel/delay actions (no js alerts in this project). On failure the
  //user STAYS on the page with the error visible — never bounced back to the list.
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const extractErrorMessage = (e: any): string =>
    e?.response?.data?.error ?? (typeof e === "string" ? e : e?.message) ?? "Something went wrong. Please try again.";

  //Shared shell for every AFTN-sending action: overlay on, run, go back on success; on failure drop the
  //overlay, surface the error inline and stay.
  const runAftnAction = async (action: () => Promise<any>) => {
    setActionError(undefined);
    setShowSendingAFTN(true);
    try {
      await action();
      goBack();
    } catch (e: any) {
      setShowSendingAFTN(false);
      setActionError(extractErrorMessage(e));
    }
  };

  //Two-step confirm for applying an IFPS-proposed route (it re-transmits to EUROCONTROL).
  const [confirmApplyRoute, setConfirmApplyRoute] = useState(false);
  const [applyingRoute, setApplyingRoute] = useState(false);
  //In-app error when applying a proposed route fails (e.g. it didn't pass IFPUV) — shown inline, not via alert().
  //`detail` is the raw IFPUV validation string (e.g. "(R)ROUTE130: UNKNOWN DESIGNATOR UN606"), shown when the
  //structured errors[] list is empty.
  const [applyError, setApplyError] = useState<{ message: string; errors?: string[]; detail?: string } | undefined>(undefined);

  const reloadPdf = async () => {
    try {
      const pdfData = await getPdfDoc();
      const pdfBlob = new Blob([pdfData], { type: "application/pdf" });
      setIcaoPdfData(pdfBlob);
    } catch {
      //no valid FPL to render (e.g. route just disarmed) — keep the last/empty preview
      setIcaoPdfData(null);
    }
  };

  useEffect(() => {
    reloadPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPdfDoc]);

  const handleRouteSelected = (result: BaseIfrSelectResult) => {
    if (result.fplFile) {
      setFplFile(result.fplFile);
    }
    setIfpsRoute(result.ifpsRoute);
    reloadPdf();
    //The route (and FPL) just changed — the previous validation is stale. Re-validate so the filing gate
    //reflects the new route (the backend already re-validated it on select; this refreshes the client).
    if (result.ifpsRoute && validateFplAction) {
      handleValidate();
    } else {
      setValidation(undefined);
    }
  };

  const fplCodeTextBoxContent = () => {
    var fpl = "";
    if (fplFile.status === "ready-to-file" || fplFile.status === "filed" || fplFile.status === "not-filed" || fplFile.status === "scheduled" || fplFile.status === "cancelled") {
      //?? "" — plans cancelled before fplCode was retained on the cancelled state have no code stored.
      fpl = fplFile.fplCode ?? "";
    } else if (fplFile.status === "delayed") {
      fpl = fplFile.initialFplCode ?? "";
    }

    if (fpl.length > 0){
      fpl = fpl.split("-").slice(0, 3).join("-") + "\n-" + fpl.split("-").slice(3, 5).join("-") + "\n-" + fpl.split("-").slice(5).join("\n-");
    }
    return fpl;
  };

  const fplCodeTextErrorFieldState = () => {
    if (fplFile.status === "error") {
      return { error: { type: "invalid", message: fplFile.error }, invalid: true, isTouched: false, isDirty: false };
    } else if (fplFile.status === "not-filed") {
      return { error: { type: "invalid", message: "Flight plan not filed" }, invalid: true, isTouched: false, isDirty: false };
    } else if (fplFile.status === "cancelled") {
      //A cancelled plan is final — say so, otherwise the page just shows an FPL with no actions and no reason.
      return { error: { type: "invalid", message: "Flight plan cancelled — file a new flight plan to fly again" }, invalid: true, isTouched: false, isDirty: false };
    }
    return { error: undefined, invalid: false, isTouched: false, isDirty: false };
  };

  const fileFplUsingAviTracer = async () => {
    await runAftnAction(fileFplUsingAviTracerAction);
  };

  const refileFpl = async () => {
    setShowRefileFplModal(false);
    await runAftnAction(refileFplAction);
  };

  const cancelFiledFpl = async () => {
    setShowCancelFplModal(false);
    await runAftnAction(cancelFiledFplAction);
  };

  const chgFpl = async (f15: string, f18: string) => {
    setShowChgFplModal(false);
    await runAftnAction(() => chgFplAction(f15, f18));
  };

  const delayFiledFpl = async (newTime: Date) => {
    setShowDelayFplModal(false);
    await runAftnAction(() => delayFiledFplAction(newTime));
  };

  //Apply the IFPS-proposed route: the backend re-validates it and sends a CHG (suspended) or a fresh FPL
  //(rejected). On failure (e.g. the proposal itself fails IFPUV) we surface the error and stay on the page.
  const applyProposedRoute = async () => {
    if (!applyProposedRouteAction || !ifrIfps?.suggestedRoute) return;
    setApplyError(undefined);
    setApplyingRoute(true);
    setShowSendingAFTN(true);
    try {
      await applyProposedRouteAction(ifrIfps.suggestedRoute);
      goBack();
    } catch (e: any) {
      //Surface the failure inline (the backend returns { error, validation.errors } on an IFPUV reject).
      const data = e?.response?.data;
      setShowSendingAFTN(false);
      setApplyingRoute(false);
      setConfirmApplyRoute(false);
      setApplyError({
        message: data?.error ?? (typeof e === "string" ? e : e?.message) ?? "Failed to apply the proposed route.",
        errors: data?.validation?.errors,
        detail: data?.validation?.validation,
      });
    }
  };

  // Route selection / IFPUV validation (before filing) and IFPS status + raw messages (after filing).
  // Rendered in the desktop right-pane "IFPS" tab, and inline in the left column on mobile.
  const ifrRouteValidationCard = !isIfr ? null : (
    <div className="border rounded-lg p-3">
      <h5 className="mb-3">IFR Route &amp; Validation</h5>

      {ifrIsFiled ? (
        <div>
          <div className="mb-1">
            <small className="text-muted">IFPS Status</small>
          </div>
          {ifrIfps ? <IfpsStateBadge ifps={ifrIfps} /> : <Badge bg="info" text="dark">Filed — awaiting IFPS</Badge>}
          {ifrIfps && (ifrIfps.ifplId || ifrIfps.ctot || ifrIfps.lastOrmType) && (
            <div className="mt-2 small">
              {ifrIfps.ifplId && <div><strong>IFPL ID:</strong> {ifrIfps.ifplId}</div>}
              {ifrIfps.ctot && <div><strong>CTOT:</strong> {ifrIfps.ctot}</div>}
              {ifrIfps.lastOrmType && <div className="text-muted">Last message: {ifrIfps.lastOrmType}</div>}
            </div>
          )}
          {ifrIfps?.errors && ifrIfps.errors.length > 0 && (ifrIfps.state === "suspended" || ifrIfps.state === "rejected") && (
            <Alert variant="danger" className="mt-2 mb-0 py-2">
              {ifrIfps.errors.map((e, i) => (
                <div key={i}>
                  <small>{e}</small>
                </div>
              ))}
            </Alert>
          )}
          {ifrIfps?.suggestedRoute && (ifrIfps.state === "suspended" || ifrIfps.state === "rejected") && (
            <Alert variant="warning" className="mt-2 mb-0 py-2">
              <small>
                <strong>Suggested route:</strong> {ifrIfps.suggestedRoute}
              </small>
              {applyProposedRouteAction && (ifrIfps.state === "suspended" || ifrIfps.state === "rejected") && (
                <div className="mt-2">
                  {!confirmApplyRoute ? (
                    <Button size="sm" variant="dark" onClick={() => { setApplyError(undefined); setConfirmApplyRoute(true); }}>
                      {ifrIfps.state === "suspended" ? "Apply route & send change (CHG)" : "Apply route & re-file"}
                    </Button>
                  ) : (
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <small className="text-dark">Re-validate with IFPS and send to EUROCONTROL?</small>
                      <Button size="sm" variant="dark" disabled={applyingRoute} onClick={applyProposedRoute}>
                        {applyingRoute ? "Sending…" : "Confirm"}
                      </Button>
                      <Button size="sm" variant="outline-secondary" disabled={applyingRoute} onClick={() => setConfirmApplyRoute(false)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                  {applyError && (
                    <Alert variant="danger" className="mt-2 mb-0 py-2">
                      <small className="d-block fw-semibold">{applyError.message}</small>
                      {applyError.errors && applyError.errors.length > 0 ? (
                        <ul className="mb-0 mt-1 ps-3">
                          {applyError.errors.map((er, i) => (
                            <li key={i}><small>{er}</small></li>
                          ))}
                        </ul>
                      ) : applyError.detail ? (
                        <small className="d-block mt-1 font-monospace" style={{ whiteSpace: "pre-wrap" }}>{applyError.detail}</small>
                      ) : null}
                    </Alert>
                  )}
                </div>
              )}
            </Alert>
          )}
          {ifpsRoute && (
            <div className="mt-2">
              <small className="text-muted">Filed route (Field 15)</small>
              <div className="font-monospace small">{ifpsRoute}</div>
            </div>
          )}
          {ifrIfps?.ormHistory && ifrIfps.ormHistory.length > 0 && (
            <IfpsMessagesList ormHistory={ifrIfps.ormHistory} />
          )}
        </div>
      ) : (
        <>
          {missingIfrAlternate && (
            <Alert variant="danger" className="py-2">
              <small>An alternate aerodrome (ALTN) is required for IFR flight plans.</small>
            </Alert>
          )}

          {missingIfrPerformance && (
            <Alert variant="danger" className="py-2">
              <small>Enter fuel burn / MTOW in the flight form — required for IFR route-finding.</small>
            </Alert>
          )}

          {ifpsRoute ? (
            <div className="mb-2">
              <small className="text-muted">Selected route (Field 15)</small>
              <div className="font-monospace small border rounded p-2 bg-light">{ifpsRoute}</div>
            </div>
          ) : (
            <div className="mb-2">
              <small className="text-muted">No IFR route selected yet. Plan a route before filing.</small>
            </div>
          )}

          {/* The route is fixed before filing — validate it here; change it by editing the flight. */}
          {ifpsRoute && validateFplAction ? (
            <>
              <div className="d-grid">
                <Button variant="dark" disabled={validating} onClick={handleValidate}>
                  {validating ? "Validating…" : "Validate with EUROCONTROL"}
                </Button>
              </div>
              {validation && (
                <Alert
                  variant={validation.result === "ok" ? "success" : "danger"}
                  className="py-2 mb-0 mt-2"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  <small>
                    {validation.result === "ok"
                      ? "No errors — IFPS valid, ready to file."
                      : validation.errors.length > 0
                      ? validation.errors.join("\n")
                      : validation.validation}
                  </small>
                </Alert>
              )}
              <div className="mt-1">
                <small className="text-muted">To change the route, edit the flight.</small>
              </div>
            </>
          ) : (
            <div className="d-grid">
              <Button
                variant="dark"
                onClick={() => setShowPlanModal(true)}
                disabled={!findIfrRoutesAction || missingIfrPerformance}
              >
                Plan IFR route
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <Modal show size="xl" scrollable contentClassName="h-100" fullscreen={"lg-down"}>
        <Modal.Header closeButton onHide={goBack}>
          <Modal.Title>
            {flightId} - {moment(departureDateTime).utc().format("DD/MM HH:mm")} - {flightPlanPicDetails}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={"p-0"}>
          <Container fluid className="h-100">
            <Row className="h-100">
              <Col xs={12} md={5} className="p-3">
                <Row className="gy-3 gx-2">
                  <Col xs={12}>
                    <MobileShowPdfButtonComponent icaoPdfData={icaoPdfData} data={data} />
                    <TextAreaBox
                      name="ICAO FPL Code"
                      value={fplCodeTextBoxContent()}
                      onChange={(v) => {}}
                      isDisabled={true}
                      rows={16}
                      fieldState={fplCodeTextErrorFieldState()}
                    />
                  </Col>
                  {actionError && (
                    <Col xs={12}>
                      <Alert variant="danger" className="py-2 mb-0" dismissible onClose={() => setActionError(undefined)}>
                        <small style={{ whiteSpace: "pre-wrap" }}>{actionError}</small>
                      </Alert>
                    </Col>
                  )}
                  <Col xs={(fplFile.status === "filed" || fplFile.status === "delayed") && !isIfr ? 9 : 12}>
                    <div className="d-grid gap-2">
                      {(fplFile.status === "ready-to-file" || fplFile.status === "scheduled") && (
                        <Button variant="dark" onClick={fileFplUsingAviTracer} disabled={!selectedOrg?.billing.canFileFpl || missingIfrAlternate || ifrBlocksFiling}>
                          {!selectedOrg?.billing.canFileFpl
                            ? "No credits available"
                            : missingIfrAlternate
                            ? "Alternate (ALTN) required for IFR"
                            : ifrBlocksFiling
                            ? (validating ? "Validating with EUROCONTROL…" : "IFPS validation required")
                            : "File FPL using AviTracer"}
                        </Button>
                      )}
                      {(fplFile.status === "filed" || fplFile.status === "delayed") && (
                        <Button variant="dark" disabled>
                          Flight Plan Filed @
                          {fplFile.status === "filed" && <>{moment(fplFile.fillingTime).utc().format(" DD/MM/YY HH:mm ")} UTC</>}
                          {fplFile.status === "delayed" && <>{moment(fplFile.initialFillingTime).utc().format(" DD/MM/YY HH:mm ")}UTC</>}
                        </Button>
                      )}
                    </div>
                  </Col>
                  {goToSchedule && (fplFile.status === "ready-to-file" || fplFile.status === "scheduled") && (
                    <Col>
                      <h4 className="pt-3 pb-0">Schedule Flight Plan File</h4>

                      {fplFile.status === "scheduled" ? (
                        <div>
                          <strong>Scheduled:</strong> {moment(fplFile.scheduleTime).utc().format("DD/MM/YY HH:mm")} UTC
                          <div className="d-grid gap-2 pt-1 pb-2">
                            <Button variant="outline-danger" onClick={goToSchedule} disabled={!selectedOrg?.billing.canFileFpl}>
                              Cancel Schedule
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="d-grid gap-2 pt-1 pb-2">
                          <Button variant="outline-dark" onClick={goToSchedule} disabled={!selectedOrg?.billing.canFileFpl}>
                            Schedule File FPL
                          </Button>
                        </div>
                      )}
                      <small className="fw-light">
                        <i>You can schedule the file of your flight plan (FPL), so it is automatically filed before your flight.</i>
                      </small>
                    </Col>
                  )}
                  {(fplFile.status === "filed" || fplFile.status === "delayed") && (
                    <>
                      {/* Re-File (and Change) are VFR-only recovery tools: IFPS already holds an IFR plan
                          (confirmed by its ACK) and would reject a re-sent identical FPL as a duplicate. */}
                      {!isIfr && (
                        <Col xs={3}>
                          <div className="d-grid gap-2">
                            <Button variant="dark" onClick={() => setShowRefileFplModal(true)}>
                              Re-File
                            </Button>
                          </div>
                        </Col>
                      )}
                      <Col xs={isIfr ? 6 : 4}>
                        <div className="d-grid gap-2">
                          <Button onClick={() => setShowDelayFplModal(true)}>Delay</Button>
                        </div>
                      </Col>
                      {!isIfr && (
                        <Col xs={4}>
                          <div className="d-grid gap-2">
                            <Button className="border" variant="light" onClick={() => setShowChgFplModal(true)}>
                              Change
                            </Button>
                          </div>
                        </Col>
                      )}
                      <Col xs={isIfr ? 6 : 4}>
                        <div className="d-grid gap-2">
                          <Button variant="danger" onClick={() => setShowCancelFplModal(true)}>
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </>
                  )}
                  {ifrRouteValidationCard && (
                    // Mobile only — on desktop the route/validation/IFPS side lives in the right-pane "IFPS" tab.
                    <Col xs={12} className="d-md-none mt-3">
                      {ifrRouteValidationCard}
                    </Col>
                  )}
                </Row>
              </Col>
              <Col md={7} className="d-none d-md-block h-100">
                {isIfr ? (
                  // IFR: tab between the IFPS side (route / validation / messages — default) and the FPL form (PDF).
                  <div className="d-flex flex-column h-100">
                    <Nav
                      variant="tabs"
                      activeKey={rightTab}
                      onSelect={(k) => k && setRightTab(k as "ifps" | "pdf")}
                      className="px-3 pt-2 flex-shrink-0"
                    >
                      <Nav.Item>
                        <Nav.Link eventKey="ifps">IFPS</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="pdf">FPL form</Nav.Link>
                      </Nav.Item>
                    </Nav>
                    {/* Both panels stay mounted and are toggled with `hidden`. Swapping them with a
                        ternary unmounted the inactive one, so every tab switch tore down and rebuilt
                        the PDF iframe — the blob URL was unchanged and nothing was re-fetched, but the
                        pane still blanked while the rebuilt frame repainted. */}
                    <div
                      className="flex-grow-1 overflow-auto p-3"
                      style={{ minHeight: 0 }}
                      hidden={rightTab !== "ifps"}
                    >
                      {ifrRouteValidationCard}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{ minHeight: 0 }}
                      hidden={rightTab !== "pdf"}
                    >
                      {icaoPdfUrl && (
                        <iframe title="pdf" width="100%" height="100%" src={icaoPdfUrl + "#toolbar=0"}></iframe>
                      )}
                    </div>
                  </div>
                ) : (
                  icaoPdfUrl && (
                    <iframe title="pdf" width="100%" height="100%" src={icaoPdfUrl + "#toolbar=0"}></iframe>
                  )
                )}
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        {showSendingAFTN && (
          <div className="w-100 h-100 position-absolute" style={{ inset: "0px", opacity: 0.8, background: "white" }}>
            <div className="d-flex flex-column align-items-center  position-absolute top-50 start-50 translate-middle">
              <Player
                autoplay
                loop
                src="/assets/brief/lottie/sending_aftn.json"
                background="transparent"
                style={{ height: "200px", width: "200px" }}
              />
              <strong>Sending AFTN message</strong>
            </div>
          </div>
        )}
      </Modal>
      {isIfr && (
        <PlanIfrRouteModal
          show={showPlanModal}
          onHide={() => setShowPlanModal(false)}
          isIfr={isIfr}
          ifpsRoute={ifpsRoute}
          departure={ifrPlanningDefaults?.departure}
          destination={ifrPlanningDefaults?.destination}
          alternate1={ifrPlanningDefaults?.alternate1}
          alternate2={ifrPlanningDefaults?.alternate2}
          findIfrRoutesAction={findIfrRoutesAction}
          checkManualRouteAction={checkManualRouteAction}
          selectIfrRouteAction={selectIfrRouteAction}
          validateFplAction={validateFplAction}
          onRouteSelected={handleRouteSelected}
        />
      )}
      <Offcanvas
        backdrop={"static"}
        show={showCancelFplModal || showDelayFplModal || showRefileFplModal || showChgFplModal}
        placement={"end"}
      >
        {showCancelFplModal && <CancelFplComponent onClose={() => setShowCancelFplModal(false)} onCancel={cancelFiledFpl} />}
        {showDelayFplModal && (
          <DelayFplComponent
            previousDepartureTime={new Date(departureDateTime)}
            onClose={() => setShowDelayFplModal(false)}
            onDelay={delayFiledFpl}
          />
        )}
        {showRefileFplModal && <RefileFplComponent onClose={() => setShowRefileFplModal(false)} onRefile={refileFpl} />}
        {showChgFplModal && <ChgFplComponent onClose={() => setShowChgFplModal(false)} onChg={chgFpl} fplFile={fplFile} />}
      </Offcanvas>
    </>
  );
}

export default BaseFplPage;

interface RefileFplComponentProps {
  onClose: () => void;
  onRefile: () => void;
}

function RefileFplComponent({ onClose, onRefile }: RefileFplComponentProps) {
  return (
    <Container className="pt-5">
      <Row>
        <Col xs={12}>
          <h3>Re-File FPL?</h3>
        </Col>
        <Col xs={12}>Are you sure you want to refile your flight plan? Refiling will resend the <strong>INITIAL plan</strong> to all AFTN addresses.<br/> Please proceed with refile <strong>ONLY if an ATC has specifically requested it</strong> due to not receiving your initial plan.</Col>
        <Row className="pt-4">
          <Col xs={6}>
            <div className="d-grid gap-2">
              <Button onClick={onRefile} variant="dark">
                Re-File FPL
              </Button>
            </div>
          </Col>
          <Col xs={6}>
            <div className="d-grid gap-2">
              <Button onClick={onClose} variant="outline-secondary">
                No
              </Button>
            </div>
          </Col>
        </Row>
      </Row>
    </Container>
  );
}

interface CancelFplComponentProps {
  onClose: () => void;
  onCancel: () => void;
}

function CancelFplComponent({ onClose, onCancel }: CancelFplComponentProps) {
  return (
    <Container className="pt-5">
      <Row>
        <Col xs={12}>
          <h3>Cancel FPL?</h3>
        </Col>
        <Col xs={12}>Are you sure you want to cancel this flight plan?</Col>
        <Row className="pt-4">
          <Col xs={6}>
            <div className="d-grid gap-2">
              <Button onClick={onCancel} variant="danger">
                Cancel FPL
              </Button>
            </div>
          </Col>
          <Col xs={6}>
            <div className="d-grid gap-2">
              <Button onClick={onClose} variant="outline-secondary">
                No
              </Button>
            </div>
          </Col>
        </Row>
      </Row>
    </Container>
  );
}

interface DelayFplComponentProps {
  onClose: () => void;
  onDelay: (newTime: Date) => void;
  previousDepartureTime: Date;
}

function DelayFplComponent({ onClose, onDelay, previousDepartureTime }: DelayFplComponentProps) {
  const previousDepartureTimeUtc = new Date(
    previousDepartureTime.getUTCFullYear(),
    previousDepartureTime.getUTCMonth(),
    previousDepartureTime.getUTCDate(),
    previousDepartureTime.getUTCHours(),
    previousDepartureTime.getUTCMinutes()
  );

  const [newDepartureTime, setNewDepartureTime] = useState(previousDepartureTimeUtc);

  return (
    <Container className="pt-5">
      <Row className="gy-4">
        <Col xs={12}>
          <h3>Delay FPL?</h3>
        </Col>
        <Col xs={12}>Are you sure you want to delay this flight plan?</Col>
        <Col xs={12}>
          <FlatPicker
            value={newDepartureTime}
            options={{
              defaultDate: newDepartureTime,
              enableTime: true,
              noCalendar: true,
              time_24hr: true,
              static: true,
            }}
            onChange={(s) => setNewDepartureTime(s[0])}
            className="form-control bg-white"
            render={({ value, ...props }, ref) => {
              return (
                <div className={"input-container rounded-lg"} style={{ backgroundColor: "#F7F7F7" }}>
                  <input ref={ref} className="bg-transparent" readOnly />
                  <label className={value ? "filled" : ""}>{"New Departure Time (UTC)"}</label>
                </div>
              );
            }}
          />
        </Col>
        <Col xs={6}>
          <div className="d-grid gap-2">
            <Button onClick={() => onDelay(new Date(newDepartureTime.getTime() - newDepartureTime.getTimezoneOffset() * 60000))}>
              Delay FPL
            </Button>
          </div>
        </Col>
        <Col xs={6}>
          <div className="d-grid gap-2">
            <Button onClick={onClose} variant="outline-secondary">
              No
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

interface ChgFplComponentProps {
  onClose: () => void;
  onChg: (f15: string, f18: string) => void;
  fplFile: FlightFplFile;
}

function ChgFplComponent({ onClose, onChg, fplFile }: ChgFplComponentProps) {

  const getFieldValue = (field: "f15" | "f18") => {
    if (fplFile.status !== "filed" && fplFile.status !== "delayed") {
      return "";
    }
    const code = fplFile.status === "filed" ? fplFile.fplCode : fplFile.initialFplCode;
    const matches = code.match(/\(FPL-(?<reg>[^-]*)-(?:[^-]*-){3}(?<dep>[^-]*)-\w* (?<f15>[^-]*)-(?<dest>[^-]{4})([^-]*-)(?<f18a>[^-]*)(?<dof>DOF\/\d{6}) ?(?<f18b>[^-]*)-/);
    if (matches && matches.groups) {
      if (field === "f18"){
        return (matches.groups.f18a ?? "") + (matches.groups.f18b ?? "");
      }
      return matches.groups[field];
    }
    return "";
  }
  
  const [initialFieldRoute] = useState<string>(getFieldValue("f15"));
  const [initialFieldOther] = useState<string>(getFieldValue("f18"));

  const [fieldRoute, setFieldRoute] = useState<string>(getFieldValue("f15"));
  const [fieldOther, setFieldOther] = useState<string>(getFieldValue("f18"));

  return (
    <Container className="pt-5">
      <Row className="gy-4">
        <Col xs={12}>
          <h3>Change FPL?</h3>
        </Col>
        <Col xs={12}>Are you sure you want to change this flight plan?</Col>
        <Col xs={12}>
          <TextAreaBox
            isDisabled={false}
            value={fieldRoute}
            onChange={(v) => setFieldRoute(v)}
            name="Route - Field 15"
            uppercase={true}
            fieldState={
              initialFieldRoute !== fieldRoute
                ? {
                    error: { message: "Previous Value: " + initialFieldRoute, type: "invalid" },
                    invalid: true,
                    isTouched: false,
                    isDirty: false,
                  }
                : undefined
            }
          />
        </Col>
        <Col xs={12}>
          <TextAreaBox
            isDisabled={false}
            value={fieldOther}
            onChange={(v) => setFieldOther(v)}
            name="Other - Field 18"
            uppercase={true}
            fieldState={
              initialFieldOther !== fieldOther
                ? {
                    error: { message: "Previous Value: " + initialFieldOther, type: "invalid" },
                    invalid: true,
                    isTouched: false,
                    isDirty: false,
                  }
                : undefined
            }
          />
        </Col>
        <Row className="pt-4">
          <Col xs={6}>
            <div className="d-grid gap-2">
              <Button onClick={() => onChg(fieldRoute, fieldOther)} variant="dark" disabled={ initialFieldOther === fieldOther && initialFieldRoute === fieldRoute }>
                Chg FPL
              </Button>
            </div>
          </Col>
          <Col xs={6}>
            <div className="d-grid gap-2">
              <Button onClick={onClose} variant="outline-secondary">
                No
              </Button>
            </div>
          </Col>
        </Row>
      </Row>
    </Container>
  );
}


interface MobileShowPdfButtonComponentProps {
  icaoPdfData: Blob | null
  data: BaseFplData
}

function MobileShowPdfButtonComponent({icaoPdfData, data}: MobileShowPdfButtonComponentProps){

  return (
    <div className="d-grid gap-2 d-md-none">
      <Button
        variant="link"
        disabled={!icaoPdfData}
        onClick={ () => {
          if (icaoPdfData) {
            var pdfURL = window.URL.createObjectURL(icaoPdfData);

            const link = document.createElement("a");
            link.download = data.flightId + "-" + moment(data.departureDateTime).utc().format("DD-MM-YY-HHmm") + "-icaoFpl.pdf";
            link.href = pdfURL;
            link.target = "_blank";
            document.body.appendChild(link);

            link.click();

            //The download/navigation has started — clean up the helper node and the object URL.
            document.body.removeChild(link);
            window.URL.revokeObjectURL(pdfURL);
          }
        }}
      >
        {icaoPdfData ? <>Show ICAO PDF</> : <>Loading PDF</>}
      </Button>
    </div>
  );
}

